// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  spacing,
  fontSize,
  fontWeight,
  borderRadius,
  mediaMaxWithLayoutTheme,
} from 'presentations/views/styles';
import {
  white,
  secondary,
  gray02,
  gray01,
  whiteFix,
} from 'presentations/views/styles/color';
import { ReactNode } from 'react';
import FormPaidIcon from 'presentations/views/components/molecules/FormPaidIcon';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import { pricingPath } from 'presentations/routes/path';
import { PlanId } from 'domains/models/subscription/planId';
import { isiOSApp as isiOSAppFunc } from 'presentations/views/helpers/electron';
import { useRouter } from 'next/router';

export type FormLabelHelp = {
  text?: string;
  url: string;
};

interface Props {
  className?: string;
  isRequired?: boolean;
  isNotRequired?: boolean;
  children?: ReactNode;
  isPaid?: boolean;
  isShowPaidLink?: boolean;
  plan?: PlanId;
  help?: FormLabelHelp;
  target?: string;
  disableContainerMargin?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const isJa = router.locale === 'ja';
  const isiOSApp = isiOSAppFunc();
  return (
    <Label
      className={props.className}
      disableContainerMargin={props.disableContainerMargin}
    >
      <TitleRow>
        {isJa && (
          <>
            {props.isRequired && <Required>{t('Required')}</Required>}
            {props.isNotRequired && <NoRequired>{t('No Required')}</NoRequired>}
          </>
        )}
        {props.children}
        {!isJa && props.isRequired && ' *'}
      </TitleRow>
      {props.isPaid && props.isShowPaidLink && props.plan && (
        <Paid
          href={pricingPath()}
          onClick={(e: any) => e.stopPropagation()}
          target={isiOSApp ? '_blank' : props.target}
        >
          <FormPaidIcon plan={props.plan} />
        </Paid>
      )}
      {props.help && (
        <HelpContainer>
          <Help href={props.help.url} target="_blank">
            <HelpIcon name="help" />
            {props.help.text}
          </Help>
        </HelpContainer>
      )}
    </Label>
  );
}

export const Label = styled.div<{ disableContainerMargin?: boolean }>`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${({ disableContainerMargin }) =>
    disableContainerMargin ? 0 : spacing(16)};
  word-break: break-all;
  /* width: 100%; */
  display: flex;
  align-items: center;
  ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
    display: block;
    font-size: ${fontSize(13)};
  }
`;
const Required = styled.span`
  color: ${whiteFix};
  font-size: ${fontSize(12)};
  background-color: ${secondary};
  border-radius: ${borderRadius()};
  padding: 2px ${spacing(4)};
  font-weight: ${fontWeight('bold')};
  margin-right: ${spacing(8)};
  white-space: nowrap;
`;
const NoRequired = styled.span`
  color: ${white};
  font-size: ${fontSize(12)};
  background-color: ${gray02};
  border-radius: ${borderRadius()};
  padding: 2px ${spacing(4)};
  font-weight: ${fontWeight('bold')};
  margin-right: ${spacing(8)};
  white-space: nowrap;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
    align-items: baseline;
  }
`;
const Paid = styled(Link)`
  margin-left: ${spacing(16)};
  ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
    margin-top: ${spacing(8)};
    margin-left: 0;
  }
`;

const HelpContainer = styled.div`
  display: flex;
  margin-left: ${spacing(8)};
  flex-grow: 1;
  text-align: right;
  font-size: ${fontSize(12)};
  font-weight: initial;
  color: ${gray01};
  vertical-align: middle;
  line-height: 24px;
  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    display: none;
  }
`;
const Help = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${gray02};
  &:hover {
    text-decoration: underline;
  }
`;
const HelpIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  width: 16px !important;
  height: 16px !important;
`;
